import {
  Component,
  computed,
  HostListener,
  inject,
  OnInit,
  Signal,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatCheckboxChange,
  MatCheckboxModule,
} from '@angular/material/checkbox';
import { MatDialogRef } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatRadioChange, MatRadioModule } from '@angular/material/radio';
import { MatSliderModule } from '@angular/material/slider';
import { ForecastSeriesCompletedWithoutMeasure } from '@api-response-types/forecast-series.response';
import { ContentMode } from '@api-types/enums/export-content-mode.enum';
import { ForecastVersionStatus } from '@api-types/enums/forecast-version-status.enum';
import { Locale } from '@api-types/enums/locale.enum';
import { Resolution } from '@api-types/enums/resolution.enum';
import { ResponseType } from '@api-types/enums/response-types.enum';
import { ExportForecastOptions } from '@api-types/types/export-forecast-options.interface';
import { TranslationSettings } from '@api-types/types/export-translation-settings.interface';
import { FilterValue } from '@api-types/types/filter-value.type';
import { DescriptionComponent } from '@components/base-elements/description.component';
import { LocaleService } from '@core/services/locale.service';
import { DecimalSeperator } from '@libs/core/enum/decimal-seperator.enum';
import {
  FormFieldComponent,
  FormInputSelectComponent,
  H2Component,
  PmDialogComponent,
} from '@libs/ui';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { FeatureFlagSelectors } from 'core/store/feature-flags/feature-flags.selectors';
import {
  differenceInDays,
  differenceInMonths,
  differenceInWeeks,
  differenceInYears,
  format,
  parseISO,
} from 'date-fns';
import { ChangeChartTimeRangeService } from 'demand-forecasting/services/chart/change-chart-time-range.service';
import {
  ExportForecastProps,
  ExportForecastService,
} from 'demand-forecasting/services/forecast-version/export-forecast.service';
import { ExportHttpClient } from 'demand-forecasting/services/http/export-http-client';
import { ConfigurationsSelectors } from 'demand-forecasting/store/configurations/configurations.selectors';
import {
  selectAppliedFilters,
  selectAvailableFilters,
} from 'demand-forecasting/store/filter/filter.selectors';
import { latestCompletedForecastSeries } from 'demand-forecasting/store/forecast-series/forecast-series.selectors';

import { selectForecastVersionByQueryParam } from 'demand-forecasting/store/forecast-version/forecast-version.selectors';
import { selectResolution } from 'demand-forecasting/store/router-state/router.selectors';
import { ToastrService } from 'ngx-toastr';
import { catchError, firstValueFrom } from 'rxjs';

type FileFormatOption = {
  label: string;
  value: string;
};
type DetailOption = {
  description: string;
  label: string;
  value: ExportForecastProps['detailMode'];
};
type ContentOption = {
  dateFcn: () => { minDate: string; maxDate: string };
  value: string;
  description: string;
  label: string;
};

type Group = {
  key: string;
  name: string;
  order: number;
};

@Component({
  selector: 'pm-export-dialog-component',
  templateUrl: './create-export-dialog.component.html',
  imports: [
    ReactiveFormsModule,
    FormsModule,
    TranslateModule,
    PmDialogComponent,
    MatSliderModule,
    MatInputModule,
    MatFormFieldModule,
    H2Component,
    MatRadioModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatCheckboxModule,
    DescriptionComponent,
    FormFieldComponent,
    FormInputSelectComponent,
  ],
})
export class CreateExportDialogComponent implements OnInit {
  private readonly translateService = inject(TranslateService);
  private readonly localeService = inject(LocaleService);
  private readonly toastService = inject(ToastrService);
  private readonly exportForecastService = inject(ExportForecastService);
  private readonly changeChartTimeRangeService = inject(
    ChangeChartTimeRangeService,
  );
  private readonly dialogRef =
    inject<MatDialogRef<CreateExportDialogComponent>>(MatDialogRef);

  private readonly store = inject(Store);

  private readonly exportHttpClient = inject(ExportHttpClient);

  protected forecastSeries = this.store.selectSignal(
    latestCompletedForecastSeries,
  );

  private readonly possibleAdditionalExportDataNames = this.store.selectSignal(
    ConfigurationsSelectors.selectAdditionalExportDataNames,
  );

  private readonly configuration = this.store.selectSignal(
    ConfigurationsSelectors.selectConfigurationByUrlParam,
  );
  private readonly selectedForecastVersion = this.store.selectSignal(
    selectForecastVersionByQueryParam,
  );
  protected resolution = this.store.selectSignal(selectResolution) as Signal<
    Resolution | undefined
  >;

  private readonly factorColumn = this.store.selectSignal(
    ConfigurationsSelectors.selectFactorColumnByUrlParam,
  );

  plnmgSapFormatFeatureFlag = this.store.selectSignal(
    FeatureFlagSelectors.selectPlnmgSAPExportFormatFeatureFlag,
  );

  periodsSapFormatFeatureFlag = this.store.selectSignal(
    FeatureFlagSelectors.selectPeriodsSAPExportFormatFeatureFlag,
  );

  chunkedExportFeatureFlag = this.store.selectSignal(
    FeatureFlagSelectors.selectChunkedExportFeatureFlag,
  );

  protected selectAdditionaTimeSeriesNames = this.store.selectSignal(
    ConfigurationsSelectors.selectAdditionalTimeSeriesNames,
  );

  contentModeEnum = ContentMode;

  detailOptions: DetailOption[] = [];
  contentOptions: ContentOption[] = [];
  fileFormatOptions: FileFormatOption[] = [];
  contentMode: ContentMode = ContentMode.FORECAST_ONLY;
  detailMode: ExportForecastProps['detailMode'] = 'AGGREGATED';
  fileFormatMode: ExportForecastProps['exportType'] = 'CSV';
  wideFormat = false;
  exportTarget = false;
  decimalDigits = 0;
  groups: Group[] = [];
  selectedGroupKeys: string[] = [];
  additionalData: { value: string; label: string }[] = [];
  selectedAdditionalData: string[] = [];
  selectedAdditionalTimeSeries: string[] = [];
  plnmgSapFormat = false;
  periodsSapFormat = false;

  appliedFilters = this.store.selectSignal(selectAppliedFilters);
  availableFilters = this.store.selectSignal(selectAvailableFilters);

  protected primaryCtaButtonConfig = computed(() => ({
    text: this.chunkedExportFeatureFlag().enabled
      ? 'RESULT.KPIS.EXPORT_OPTIONS.GENERATE_EXPORT'
      : 'RESULT.KPIS.EXPORT_OPTIONS.EXPORT',
    isLoading: this.isExporting() || !this.forecastSeries(),
    disabled: !this.isDecimalDigitsValid(),
    dataTestid: 'export-submit-button',
    handleClick: () => this.submitExport(),
  }));

  protected generateExportStarted: boolean;

  ngOnInit(): void {
    this.generateExportStarted = false;
    // problem: res can be undefined! I want to call this init function only once if res is not undefined
    const forecastSeries = this.forecastSeries();
    if (!forecastSeries) {
      console.error('No forecast series found in export modal. Abort init');
    } else {
      this.setupExportOptions(forecastSeries);
    }
    this.getAdditionalExportDataNames();
  }

  @HostListener('document:keydown.escape') onKeydownHandler() {
    this.dialogRef.close();
  }

  getAdditionalExportDataNames() {
    const forecastSeries = this.forecastSeries();
    if (!forecastSeries) {
      console.error('No forecast series found in export modal. Abort init');
      return;
    }
    const tempadditionalData = this.possibleAdditionalExportDataNames()?.map(
      (additionalExportDataName) => ({
        value: additionalExportDataName,
        label: additionalExportDataName,
      }),
    );
    // TODO fix this error handling
    if (!tempadditionalData) {
      console.error(
        'No possible extra forecasts found in export modal. Abort init',
      );
      return;
    }
    this.additionalData = tempadditionalData;
  }

  setupExportOptions(
    latestForecastSeries: ForecastSeriesCompletedWithoutMeasure,
  ): void {
    this.detailOptions = [
      {
        label: 'RESULT.KPIS.EXPORT_OPTIONS.LEVEL_OF_DETAIL.AGGREGATED.LABEL',
        value: 'AGGREGATED',
        description:
          'RESULT.KPIS.EXPORT_OPTIONS.LEVEL_OF_DETAIL.AGGREGATED.DESCRIPTION',
      },
      {
        label: 'RESULT.KPIS.EXPORT_OPTIONS.LEVEL_OF_DETAIL.BREAKDOWN.LABEL',
        value: 'DETAILED',
        description:
          'RESULT.KPIS.EXPORT_OPTIONS.LEVEL_OF_DETAIL.BREAKDOWN.DESCRIPTION',
      },
    ];

    this.contentOptions = [
      {
        label: 'RESULT.KPIS.EXPORT_OPTIONS.CONTENT.FORECAST.LABEL',
        value: ContentMode.FORECAST_ONLY,
        description: 'RESULT.KPIS.EXPORT_OPTIONS.CONTENT.FORECAST.DESCRIPTION',
        dateFcn: () => this.getForecastTimeRange(latestForecastSeries),
      },
      {
        label: 'RESULT.KPIS.EXPORT_OPTIONS.CONTENT.FORECAST_HISTORY.LABEL',
        value: ContentMode.FORECAST_AND_HISTORY,
        description:
          'RESULT.KPIS.EXPORT_OPTIONS.CONTENT.FORECAST_HISTORY.DESCRIPTION',
        dateFcn: () =>
          this.getForecastWithHistoryTimeRange(latestForecastSeries),
      },
      {
        label: 'RESULT.KPIS.EXPORT_OPTIONS.CONTENT.SELECTED.LABEL',
        value: ContentMode.SELECTED_TIMERANGE,
        description: 'RESULT.KPIS.EXPORT_OPTIONS.CONTENT.SELECTED.DESCRIPTION',
        dateFcn: () => this.getSelectedTimeRange(),
      },
    ];

    this.fileFormatOptions = [
      {
        label: 'RESULT.KPIS.EXPORT_OPTIONS.FILE_FORMAT.CSV',
        value: 'CSV',
      },
      {
        label: 'RESULT.KPIS.EXPORT_OPTIONS.FILE_FORMAT.XLSX',
        value: 'XLSX',
      },
      {
        label: 'RESULT.KPIS.EXPORT_OPTIONS.FILE_FORMAT.ZIP',
        value: 'ZIP',
      },
    ];

    // Setup Groups with "translations" for checkboxes
    const filteredCategoricals: { [key: string]: string } = {};
    this.availableFilters().forEach((value) => {
      if (value.column) {
        filteredCategoricals[value.column] = value.column;
      }
    });
    this.groups = this.sortByConfiguredGroupColumns(
      Object.keys(filteredCategoricals),
    ).map((key, order) => ({
      key,
      name: filteredCategoricals[key],
      order,
    }));
    this.selectedGroupKeys = this.groups.map((group) => group.key);
  }

  private sortByConfiguredGroupColumns(keys: string[]): string[] {
    const groupColumns =
      this.configuration()?.startForecastConfig.group_columns ?? [];
    const orderOf = (key: string) => {
      const index = groupColumns.indexOf(key);
      return index === -1 ? groupColumns.length : index;
    };
    return [...keys].sort((a, b) => orderOf(a) - orderOf(b));
  }

  isExporting() {
    return this.exportForecastService.isLoading();
  }

  isDecimalDigitsValid(): boolean {
    return (
      this.decimalDigits !== null &&
      this.decimalDigits !== undefined &&
      this.decimalDigits >= 0 &&
      this.decimalDigits <= 30 &&
      Number.isInteger(this.decimalDigits)
    );
  }

  getForecastWithHistoryTimeRange(
    forecastSeries: ForecastSeriesCompletedWithoutMeasure,
  ) {
    const minDate = forecastSeries.originalDates[0][1];
    const maxDate =
      forecastSeries.originalDates[forecastSeries.originalDates.length - 1][1];

    return {
      minDate,
      maxDate,
    };
  }

  getForecastTimeRange(forecastSeries: ForecastSeriesCompletedWithoutMeasure) {
    const minDate =
      forecastSeries.originalDates[forecastSeries.initialForecastIndex][1];
    const maxDate =
      forecastSeries.originalDates[forecastSeries.originalDates.length - 1][1];

    return {
      minDate,
      maxDate,
    };
  }

  calculateCalendarWeek(dateString: string): string {
    const date = parseISO(dateString);
    return format(date, "yyyy-'W'II");
  }

  getSelectedTimeRange() {
    const { timeRange } = this.changeChartTimeRangeService;
    const dateMin = new Date(timeRange.min);
    const dateMax = new Date(timeRange.max);

    const minDate = dateMin.toISOString().split('T')[0];
    const maxDate = dateMax.toISOString().split('T')[0];

    return {
      minDate,
      maxDate,
    };
  }

  transformDateToYearMonth(dateString: string): string {
    if (dateString && dateString.length >= 7) {
      return dateString.slice(0, 7);
    }
    return dateString;
  }

  transformDateToYear(dateString: string): string {
    if (dateString && dateString.length >= 4) {
      return dateString.slice(0, 4);
    }
    return dateString;
  }

  private calculateDateColumnCount(
    minDate: string,
    maxDate: string,
    resolution: Resolution | undefined,
  ): number {
    const start = parseISO(minDate);
    const end = parseISO(maxDate);

    switch (resolution) {
      case Resolution.Daily:
        return differenceInDays(end, start) + 1;
      case Resolution.Weekly:
        return differenceInWeeks(end, start) + 1;
      case Resolution.Monthly:
        return differenceInMonths(end, start) + 1;
      case Resolution.Yearly:
        return differenceInYears(end, start) + 1;
      default:
        // Default to daily if resolution is undefined
        return differenceInDays(end, start) + 1;
    }
  }

  private async validateWideFormatExport(): Promise<{
    valid: boolean;
    columnCount: number;
    message?: string;
  }> {
    if (!this.wideFormat) {
      return { valid: true, columnCount: 0 };
    }

    const forecastSeries = this.forecastSeries();
    if (!forecastSeries) {
      return { valid: true, columnCount: 0 };
    }

    // Get date range based on content mode
    let minDate: string;
    let maxDate: string;

    if (this.contentMode === ContentMode.SELECTED_TIMERANGE) {
      ({ minDate, maxDate } = this.getSelectedTimeRange());
    } else if (this.contentMode === ContentMode.FORECAST_ONLY) {
      ({ minDate, maxDate } = this.getForecastTimeRange(forecastSeries));
    } else {
      ({ minDate, maxDate } =
        this.getForecastWithHistoryTimeRange(forecastSeries));
    }

    const resolution = this.resolution();
    const dateColumns = this.calculateDateColumnCount(
      minDate,
      maxDate,
      resolution,
    );

    // Excel column limit
    const EXCEL_MAX_COLUMNS = 16384;
    // Estimate base columns (SKU, Location, KPIs, etc.)
    const numberOfSelectedGroups =
      this.detailMode === 'DETAILED' ? this.selectedGroupKeys.length : 1;

    const totalColumns = numberOfSelectedGroups + dateColumns;

    if (totalColumns > EXCEL_MAX_COLUMNS) {
      return {
        valid: false,
        columnCount: totalColumns,
        message: await firstValueFrom(
          this.translateService.get(
            'RESULT.KPIS.EXPORT_OPTIONS.WIDE_FORMAT_COLUMN_LIMIT_ERROR',
            {
              totalColumns,
              maxColumns: EXCEL_MAX_COLUMNS,
            },
          ),
        ).catch(
          () =>
            `Wide format would generate ${totalColumns} columns, exceeding Excel's limit of ${EXCEL_MAX_COLUMNS}. Please reduce the date range or use a coarser resolution.`,
        ),
      };
    }

    // Warning for large but valid exports
    if (totalColumns > 5000) {
      return {
        valid: true,
        columnCount: totalColumns,
        message: await firstValueFrom(
          this.translateService.get(
            'RESULT.KPIS.EXPORT_OPTIONS.WIDE_FORMAT_LARGE_WARNING',
            {
              totalColumns,
            },
          ),
        ).catch(
          () =>
            `This export will generate ${totalColumns} columns and may take several minutes to process.`,
        ),
      };
    }

    return { valid: true, columnCount: totalColumns };
  }

  async submitExport() {
    // Validate wide format if enabled
    const validation = await this.validateWideFormatExport();

    if (!validation.valid) {
      // Show error dialog - block export
      this.toastService.error(
        validation.message || 'Wide format validation failed',
        this.translateService.instant(
          'RESULT.KPIS.EXPORT_OPTIONS.EXPORT_ERROR_TITLE',
        ),
        {
          timeOut: 0,
          extendedTimeOut: 0,
          closeButton: true,
        },
      );
      return;
    }
    if (validation.message && validation.columnCount > 5000) {
      // Show warning toast - but allow export to proceed
      this.toastService.warning(
        validation.message,
        this.translateService.instant(
          'RESULT.KPIS.EXPORT_OPTIONS.LARGE_EXPORT_WARNING_TITLE',
        ),
        {
          timeOut: 10000,
          closeButton: true,
        },
      );
    }

    // check feature flag chunked_export -> if true call chunkedExport, if false call generalExport
    const flag = this.chunkedExportFeatureFlag();
    if (flag.enabled) {
      this.chunkedExport();
    } else {
      this.generalExport();
    }
  }

  async chunkedExport() {
    const forecastVersion = this.selectedForecastVersion();
    if (!forecastVersion) {
      throw new Error('No forecast version selected');
    }
    const exportOptions = await this.getExportForecastOptions();
    if (!exportOptions) {
      return;
    }

    this.exportHttpClient
      .generateExport(forecastVersion.id, exportOptions)
      .subscribe({
        next: () => {
          this.generateExportStarted = true;
        },
        error: (error) => {
          this.generateExportStarted = false;
          throw error;
        },
      });
  }

  async generalExport() {
    const forecastVersion = this.selectedForecastVersion();
    if (!forecastVersion) {
      throw new Error('No forecast version selected');
    }
    const configuration = this.configuration();
    if (!configuration) {
      throw new Error('No configuration found');
    }
    const exportOptions = await this.getExportForecastOptions();
    if (!exportOptions) {
      return;
    }

    this.exportForecastService
      .exportForecast(forecastVersion.id, exportOptions)
      .pipe(
        catchError((error) => {
          this.toastService.error(
            this.translateService.instant('RESULT.KPIS.ERROR_EXPORT.BODY'),
            this.translateService.instant('RESULT.KPIS.ERROR_EXPORT.TITLE'),
          );
          throw error;
        }),
      )
      .subscribe((res) => {
        if (res.body === null) {
          throw new Error('No response body');
        }
        const resType = res.body.type;
        let exportType = 'CSV';
        if (resType === ResponseType.XLSX) {
          exportType = 'XLSX';
        } else if (
          resType === ResponseType.TXT ||
          resType === ResponseType.TXT_UTF8
        ) {
          exportType = 'TXT';
        } else if (resType === ResponseType.ZIP) {
          exportType = 'ZIP';
        }

        if (exportType !== this.fileFormatMode) {
          this.toastService.warning(
            this.translateService.instant(
              'RESULT.KPIS.EXPORT_XLSX_TOO_BIG.BODY',
            ),
            this.translateService.instant(
              'RESULT.KPIS.EXPORT_XLSX_TOO_BIG.TITLE',
            ),
            {
              timeOut: 0,
              extendedTimeOut: 0,
              closeButton: true,
            },
          );
        }

        this.exportForecastService
          .downloadFile(
            res.body,
            exportType,
            configuration.name,
            forecastVersion.createdAt,
            this.periodsSapFormat,
            this.forecastSeries(),
          )
          .then(() => {
            this.dialogRef.close();
          })
          .catch((error) => {
            console.error('Error downloading file:', error);
            this.dialogRef.close();
          });
      });
  }

  async getExportForecastOptions(): Promise<ExportForecastOptions | null> {
    const forecastSeries = this.forecastSeries();
    if (!forecastSeries) {
      console.error('No forecast series found in export modal. Abort init');
      return null;
    }
    if (forecastSeries._resultSetStatus !== ForecastVersionStatus.FINISHED) {
      return null;
    }
    let selectedGroups = this.groups
      .map((group) => group.key)
      .filter((key) => this.selectedGroupKeys.includes(key));
    if (this.detailMode !== 'DETAILED') {
      selectedGroups = [];
    }

    if (this.plnmgSapFormat || this.periodsSapFormat) {
      this.wideFormat = false;
    }

    let { minDate, maxDate } =
      this.getForecastWithHistoryTimeRange(forecastSeries);
    if (this.contentMode === ContentMode.SELECTED_TIMERANGE) {
      ({ minDate, maxDate } = this.getSelectedTimeRange());
    } else if (this.contentMode === ContentMode.FORECAST_ONLY) {
      ({ minDate, maxDate } = this.getForecastTimeRange(forecastSeries));
    }

    const xlsxTranslationsPath = 'RESULT.KPIS.EXPORT_OPTIONS.XLSX_METADATA';
    let xlsxMetadataTranslationObject = {};
    try {
      xlsxMetadataTranslationObject = await firstValueFrom(
        this.translateService.get(xlsxTranslationsPath),
      );
    } catch (error) {
      console.error('Error fetching translation:', error);
    }

    const resolution = this.resolution();

    const configuration = this.configuration();
    if (!configuration) {
      throw new Error('No configuration found');
    }
    const forecastVersion = this.selectedForecastVersion();
    if (!forecastVersion) {
      throw new Error('No forecast version selected');
    }

    if (!resolution) {
      throw new Error('No resolution found');
    }
    const filters: Record<string, FilterValue[]> = {};
    this.appliedFilters().forEach((value, key) => {
      filters[key] = value;
    });

    // selectedAdditionalTimeSeries only those that are selected in export additional time series AND are present in selectAdditionaTimeSeriesNames
    const selectedAdditionalTimeSeries = this.selectedAdditionalData.filter(
      (dataPoint) => this.selectAdditionaTimeSeriesNames().includes(dataPoint),
    );

    return {
      exportType: this.fileFormatMode,
      detailMode: this.detailMode.toUpperCase(),
      forecastId: forecastVersion.forecastId,
      filters,
      resolution,
      resultIds: ['aggregated'], // not used now...
      translationSettings: this.getTranslationSettings(),
      decimalSeperator: this.getDecimalSeparator(),
      xlsxMetadataTranslationObject,
      contentMode: this.contentMode,
      minDate,
      maxDate,
      groups: selectedGroups,
      selectedAdditionalData: this.selectedAdditionalData,
      selectedAdditionalTimeSeries,
      wideFormat: this.wideFormat,
      exportTarget: this.exportTarget,
      plnmgSapFormat: this.plnmgSapFormat,
      periodsSapFormat: this.periodsSapFormat,
      factorColumn: this.factorColumn(),
      valueSeparator: this.getValueSeparator(),
      decimalDigits: this.decimalDigits,
    };
  }

  getTranslationSettings(): TranslationSettings {
    return {
      translations: {
        prediction: this.translateService.instant('RESULT.EXPORT.PREDICTION'),
        manualPrediction: this.translateService.instant(
          'RESULT.EXPORT.MANUAL_PREDICTION',
        ),
        comment: this.translateService.instant('RESULT.EXPORT.COMMENT'),
      },
    };
  }

  getDecimalSeparator(): DecimalSeperator {
    return this.localeService.getCurrentUserLocale() === Locale.GERMAN
      ? DecimalSeperator.COMMA
      : DecimalSeperator.POINT;
  }

  getValueSeparator(): ';' | ',' {
    return this.localeService.getCurrentUserLocale() === Locale.GERMAN
      ? ';'
      : ',';
  }

  onContentModeChange(event: MatRadioChange) {
    switch (event.value) {
      case ContentMode.FORECAST_AND_HISTORY:
        this.exportTarget = true;
        break;
      case ContentMode.FORECAST_ONLY:
        this.exportTarget = false;
        break;
      case ContentMode.SELECTED_TIMERANGE: {
        const forecastSeries = this.forecastSeries();
        if (!forecastSeries) {
          console.error('No forecast series found in export modal. Abort init');
          return;
        }
        const minDate = new Date(this.getSelectedTimeRange().minDate);
        const forecastStartDate = new Date(
          forecastSeries.predictions.data[
            forecastSeries.initialForecastIndex
          ][0],
        );
        this.exportTarget = minDate.getTime() < forecastStartDate.getTime();
        break;
      }
      default:
        console.error('Unknown content mode:', event.value);
    }
  }

  onPlnmgSapFormatCheckboxChange(event: MatCheckboxChange) {
    this.wideFormat = false;
    this.selectedAdditionalData = [];
    if (event.checked) {
      this.detailMode = 'DETAILED';
      this.selectedAdditionalData = this.additionalData.map(
        (datapoint) => datapoint.value,
      );
    }
  }

  onPeriodsSapFormatCheckboxChange(event: MatCheckboxChange) {
    this.wideFormat = false;
    this.exportTarget = false;
    this.selectedAdditionalData = [];
    if (event.checked) {
      this.detailMode = 'DETAILED';
      this.contentMode = ContentMode.FORECAST_ONLY;
      // this.fileFormatMode = 'TXT';
      this.fileFormatMode = 'ZIP';
      this.selectedGroupKeys = this.selectedGroupKeys.filter(
        (key) => key !== 'Sales district',
      );

      this.selectedAdditionalData = this.additionalData.reduce<string[]>(
        (accumulator, dataPoint) => {
          if (
            dataPoint.value === 'Vertriebsweg' ||
            dataPoint.value === 'Planer-Nr.'
          ) {
            accumulator.push(dataPoint.value);
          }
          return accumulator;
        },
        [],
      );
    }
  }
}
